import React, { useEffect, useState, useMemo } from 'react';
import { useToggle, useStores } from 'hooks';
import { observer } from 'mobx-react-lite';
import { Icon } from '../../../shared';
import { toJS } from 'mobx';
import { BidActivityLanes } from './BidActivityLanes';
import {
  IconButton,
  Section,
  SectionContent,
  SectionHeader,
  SectionHeaderLabel,
  Track,
  TrackKey,
  TrackValue,
} from '../Styles';
import {Select, Option, SelectContainer, Label} from './Styles';


const NotificationsList = observer(({auctionID, activitySectionOpen}) => {
  const {
    UIStore: {
      audioLane,
      audioAuction
    },
    AuctionStore: {
      auctionInfo, bidActivityLanes, openLanes, activeBidActivityLaneId, setActiveBidActivityLaneId
    },
    BidStore: { notifications },
  } = useStores(auctionID);

  const [laneId, setLaneID] = useState(activeBidActivityLaneId);

  const laneFilter = (item) => {
    if (item?.LaneID === laneId) {
      return true;
    }

    if (!laneId && openLanes[0]) {
      return item?.LaneID === openLanes[0];
    }

    return item?.LaneID === laneId;
  };

  useEffect(() => {}, [openLanes.length]);

  useEffect(() => {
    setActiveBidActivityLaneId(audioLane);
    setLaneID(audioLane);
    // eslint-disable-next-line
  }, [audioLane]);

  useEffect(() => {
    setLaneID(openLanes[0])
  }, []);

  if (openLanes.length === 0) {
    return null;
  }

  return (
      <>
        {activitySectionOpen && (
            <>
              <BidActivityLanes
                  activitySectionOpen={activitySectionOpen}
                  auctionID={auctionID}
                  laneId={laneId}
                  setLaneID={setLaneID}
              />

              <SectionContent height="230px">
                {notifications
                    .filter(laneFilter)
                    .slice(0, 50)
                    .map((value, index) => (
                        <Track color="var(--bidActivityTextColor)" bgColor1="var(--bidActivityBgColor1)" bgColor2="var(--bidActivityBgColor2)" key={index}>
                          <TrackKey>{value.timestamp}</TrackKey>
                          <TrackValue>
                            {value.notification}
                          </TrackValue>
                        </Track>
                    ))}
              </SectionContent>
            </>
        )}
      </>
  )
})

export const BidActivity = observer(() => {
  const {
    MultiAuctionStore: { auctions, allAuctionsAndLanes },
    UIStore: { audioAuction, setAudioAuction },
  } = useStores();

  const [connectedAuctions, setConnectedAuctions] = useState([]);
  const [selectedAuction, setSelectedAuction] = useState(audioAuction);

  const getToggleIcon = (isOpen) => (isOpen ? 'chevron-up' : 'chevron-down');
  const { isToggled: activitySectionOpen, toggle: toggleActivitySectContent } = useToggle(false);
  /** Effect: Filter connected auctions */

  useEffect(() => {
    const auctionEntries = Array.from(auctions.entries() ?? []);

    const filteredAuctions = auctionEntries
        .filter(([, auctionStore]) => auctionStore.connected && auctionStore.openLanes.length)
        .map(([key, auctionStore]) => ({
          auctionID: key,
          auctionName: getAuctionNameByID(key) || auctionStore.auctionInfo?.auction || auctionStore?.auctionName,
        }));

    setConnectedAuctions(filteredAuctions);
  }, [JSON.stringify(toJS(auctions))]);

  const getAuctionNameByID = (id) => {
    const auction = allAuctionsAndLanes.find((auction) => auction.auctionID === id);
    return auction?.auction;
  }

  /** Effect: Sync selected auction with audioAuction */
  useEffect(() => {
    if (audioAuction) {
      setSelectedAuction(audioAuction);
    }
  }, [audioAuction]);

  /** Handle Auction Change */
  const handleAuctionChange = (event) => {
    const newAuctionID = event.target.value;
    setSelectedAuction(newAuctionID);
  };

  if (!audioAuction) {
    return null;
  }

  return (
      <Section>
        <SectionHeader>
          <SectionHeaderLabel>
            <Icon icon="gavel" size={1} hoverPointer={false} />
            Bidding Activity
          </SectionHeaderLabel>
          <IconButton onClick={toggleActivitySectContent}>
            <Icon icon={getToggleIcon(activitySectionOpen)} iconOnly size={0.8} />
          </IconButton>
        </SectionHeader>

        {/* Select Box to Choose Auction */}
        <SelectContainer>
          <Label>Auction</Label>

          <Select
              id="auctionSelect"
              value={selectedAuction || ""}
              onChange={handleAuctionChange}
          >
            {connectedAuctions.map(({ auctionID, auctionName }) => (
                <Option key={auctionID} value={auctionID}>
                  {auctionName}
                </Option>
            ))}
          </Select>
        </SelectContainer>

        {/* Pass Selected Auction to NotificationsList */}
        {selectedAuction && <NotificationsList auctionID={selectedAuction} activitySectionOpen={activitySectionOpen} />}
      </Section>
  );
});

export default BidActivity;
